'use client'

import Link from 'next/link'

import { KineticWorkshopFooter } from '../../../components/kinetic/KineticWorkshopFooter'
import { Button } from '../../../components/ui/button'

const curriculum = [
  {
    title: '01 Foundations of Data Engineering',
    desc: 'Week 1-2 · Intro to lifecycle, Python, SQL, Docker & VS Code',
  },
  {
    title: '02 Distributed Systems with Spark',
    desc: 'Week 2 · Apache Spark, HDFS, performance tuning',
  },
  {
    title: '03 Cloud Data Warehousing & Lakes',
    desc: 'Week 3 · S3, Redshift, BigQuery, data modeling',
  },
  { title: '04 Orchestration & Final Project', desc: 'Week 5-6 · Airflow, capstone project' },
]

const whoIsFor = [
  { title: 'Recent Grads', desc: 'CS or IT graduates looking to specialize in data.' },
  { title: 'Software Devs', desc: 'Developers pivoting to data engineering roles.' },
  { title: 'Data Analysts', desc: 'Analysts looking to master the engineering stack.' },
]

export default function GraduateWorkshopPage() {
  return (
    <div className="min-h-screen bg-kinetic-surface text-kinetic-on-surface">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-24 sm:px-12 lg:px-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl flex-1">
            <h1 className="font-headline text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
              Graduate Data Engineering Workshop
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-kinetic-on-surface-variant">
              Fast-track your career in the cloud. Master Python, SQL, Spark, and Cloud
              Infrastructure in 6 weeks with hands-on labs and expert mentorship.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link href="/workshops/register">
                <Button variant="kinetic" size="lg">
                  Register Now
                </Button>
              </Link>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="border-kinetic-outline-variant/40 rounded-none bg-transparent text-kinetic-on-surface hover:bg-kinetic-surface-container-low hover:text-white"
              >
                Download Syllabus
              </Button>
            </div>
          </div>
          <div className="border-kinetic-outline-variant/30 flex-shrink-0 border bg-kinetic-surface-container-low p-7 lg:w-80">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-kinetic-on-surface-variant">
              Next Start: Oct 16, 2023
            </p>
            <p className="font-headline text-3xl font-bold text-white">$1,499</p>
            <p className="mt-2 text-sm text-kinetic-on-surface-variant">
              6 Weeks · Virtual (Live) · Intermediate
            </p>
            <Link href="/workshops/register" className="mt-4 block">
              <Button variant="kinetic" size="lg" className="w-full">
                Secure Your Spot
              </Button>
            </Link>
            <p className="mt-3 font-mono text-xs uppercase tracking-wider text-kinetic-outline">
              Limited to 25 seats
            </p>
          </div>
        </div>
      </section>

      {/* What You Learn */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <h2 className="mb-8 font-headline text-2xl font-bold uppercase tracking-tight text-white">
          What you will learn
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: 'Advanced Python & SQL',
              desc: 'Data manipulation and query optimization for production.',
            },
            { title: 'Cloud Infrastructure', desc: 'AWS and Azure: S3, Lambdas, Synapse.' },
            { title: 'Distributed Computing', desc: 'Apache Spark, HDFS, performance tuning.' },
            { title: 'ETL & Pipelines', desc: 'Airflow and modern orchestration tools.' },
          ].map(item => (
            <div
              key={item.title}
              className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-low p-6"
            >
              <div className="mb-3 h-8 w-8 bg-kinetic-primary-container" />
              <h3 className="font-headline text-base font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-kinetic-on-surface-variant">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Who Is This For */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <h2 className="mb-8 font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Who is this workshop for?
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {whoIsFor.map(item => (
            <div
              key={item.title}
              className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-low p-6"
            >
              <div className="mb-3 h-7 w-7 bg-kinetic-primary-container" />
              <h3 className="font-headline text-base font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-kinetic-on-surface-variant">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Curriculum */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
            Detailed Curriculum
          </h2>
          <button
            type="button"
            className="w-fit font-headline text-sm font-semibold uppercase tracking-tight text-kinetic-primary-container hover:underline"
          >
            Download PDF
          </button>
        </div>
        <div className="space-y-3">
          {curriculum.map(item => (
            <div
              key={item.title}
              className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-low p-5"
            >
              <h3 className="font-headline text-base font-semibold text-white">{item.title}</h3>
              <p className="mt-1 text-sm text-kinetic-on-surface-variant">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Instructor */}
      <section className="border-kinetic-outline-variant/30 border-t bg-kinetic-surface-container-low py-16">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-6 sm:px-12 md:flex-row lg:px-16">
          <div className="border-kinetic-outline-variant/30 h-24 w-24 flex-shrink-0 rounded-full border bg-kinetic-surface-container-highest" />
          <div>
            <h2 className="mb-2 font-headline text-2xl font-bold uppercase tracking-tight text-white">
              Your Instructor
            </h2>
            <h3 className="font-headline text-lg font-semibold text-white">Sarah Jenkins</h3>
            <p className="font-mono text-sm uppercase tracking-wider text-kinetic-primary-container">
              Senior Cloud Architect at Cloud Shuttle
            </p>
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-kinetic-on-surface-variant">
              Sarah has over 10 years building petabyte-scale data platforms for fintech and
              aerospace. AWS Certified Solutions Architect and Apache Airflow contributor.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16 text-center sm:px-12 lg:px-16">
        <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Ready to level up your data career?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-base text-kinetic-on-surface-variant">
          Join the next cohort of data engineers building the future of cloud infrastructure.
        </p>
        <Link href="/workshops/register" className="mt-6 inline-block">
          <Button variant="kinetic" size="lg">
            Register Now
          </Button>
        </Link>
      </section>

      <KineticWorkshopFooter />
    </div>
  )
}
